import {
  DealTermsTableLayout,
  ParagraphSectionLayout,
  ParagraphTitle,
} from "@frontend/common";

import { getAxisRoleLabel, useAxisRoleQuery } from "@api/axisRole/axisRole";
import { DEALS_PARAGRAPH_CODE } from "@EditArticleContainer/shared/constants/editArticle.constants";
import { useCreateCompanyLogoComponentList } from "@EditArticleContainer/shared/hooks/useCreateCompanyLogoComponentList";
import { useParagraphEditedData } from "@EditArticleContainer/shared/hooks/useParagraph";
import { useUpdateParagraph } from "@EditArticleContainer/shared/hooks/useUpdateParagraph";
import {
  REF_CODE_DEAL_LICENSEE_NAME_MA,
  REF_CODE_DEAL_LICENSEE_RESPONSIBILITY_MA,
  REF_CODE_DEAL_LICENSOR_NAME_MA,
  REF_CODE_DEAL_LICENSOR_RESPONSIBILITY_MA,
} from "@EditArticleContainer/shared/libs/refCode/constants/refCode.constants";
import { createEditArticleShellList } from "@EditArticleContainer/shared/utils/createEditArticleShellList";

import * as S from "./DealsParagraphPartnerAxisMA.style";
import {
  licenseeNameLabels,
  licenseeNameText,
  licenseeResponsibilityLabels,
  licenseeResponsibilityText,
  licensorNameLabels,
  licensorNameText,
  licensorResponsibilityLabels,
  licensorResponsibilityText,
} from "./utils/getPartnerAxisMAShellValue";

const PARAGRAPH_TITLE = "Deal Terms";

const DealsParagraphPartnerAxisMA = () => {
  const paragraph = useParagraphEditedData(DEALS_PARAGRAPH_CODE.partnerAxisMA);
  const [updateParagraph, loadingIndex, updateKeyMap] = useUpdateParagraph({
    paragraphCode: DEALS_PARAGRAPH_CODE.partnerAxisMA,
    sectionCode: "TY002",
  });
  const { createCompanyLogoComponentList } =
    useCreateCompanyLogoComponentList();
  const { data: axisRole } = useAxisRoleQuery();

  if (!paragraph) return null;

  const licensorName = licensorNameText(paragraph);
  const licenseeName = licenseeNameText(paragraph);

  // 타이틀을 "Partner A (Axis Role)" 형태로 구성한다. Role이 없으면 라벨만.
  const toPartnerTitle = (label: string, axis: string) => {
    const role = getAxisRoleLabel(axisRole, axis);
    if (!role) return label;
    return (
      <>
        {label}
        <S.TitleAxisRole>({role})</S.TitleAxisRole>
      </>
    );
  };

  const licensorColumn = createEditArticleShellList([
    {
      title: toPartnerTitle("Partner A", "A"),
      text: licensorName,
      labels: licensorNameLabels(paragraph),
      refKey: REF_CODE_DEAL_LICENSOR_NAME_MA,
      imagePosition: "left",
      imageNodeList: createCompanyLogoComponentList(
        licensorName.replaceAll("- ", "").split("\n"),
      ),
      isDisabled: true,
    },
    {
      title: "Responsibility & Rights",
      text: licensorResponsibilityText(paragraph),
      labels: licensorResponsibilityLabels(paragraph),
      refKey: REF_CODE_DEAL_LICENSOR_RESPONSIBILITY_MA,
      displayEmpty: !!licensorName,
    },
  ]);

  const licenseeColumn = createEditArticleShellList([
    {
      title: toPartnerTitle("Partner B", "B"),
      text: licenseeName,
      labels: licenseeNameLabels(paragraph),
      refKey: REF_CODE_DEAL_LICENSEE_NAME_MA,
      imagePosition: "left",
      imageNodeList: createCompanyLogoComponentList(
        licenseeName.replaceAll("- ", "").split("\n"),
      ),
      isDisabled: true,
    },
    {
      title: "Responsibility & Rights",
      text: licenseeResponsibilityText(paragraph),
      labels: licenseeResponsibilityLabels(paragraph),
      refKey: REF_CODE_DEAL_LICENSEE_RESPONSIBILITY_MA,
      displayEmpty: !!licenseeName,
    },
  ]);

  const tableItems = [licensorColumn, licenseeColumn].filter(
    (col) => col.length > 0,
  );

  if (tableItems.length === 0) return null;

  return (
    <ParagraphSectionLayout>
      <ParagraphTitle title={PARAGRAPH_TITLE} />
      <S.ParagraphPartnerAxisMAWrapper key={updateKeyMap.get(0)}>
        <S.ParagraphPartnerAxisMAUpdateButton
          disabled={loadingIndex === 0}
          onClick={() => updateParagraph(0)}
        />
        <DealTermsTableLayout items={tableItems} isEditable />
      </S.ParagraphPartnerAxisMAWrapper>
    </ParagraphSectionLayout>
  );
};

export default DealsParagraphPartnerAxisMA;
